/**
 * error codes used for FBAdLoader
 */
const AdErrorCodes = Object.freeze({
  TIME_OUT: 'TIME_OUT',
  NO_ERROR_CODE: 'NO_ERROR_CODE',
  ADS_NO_FILL: 'ADS_NO_FILL',
  USER_INPUT: 'USER_INPUT'
});
/**
 * ad error event names.
 */

const AdErrorEvents = Object.freeze({
  AD_SHOW_FAIL: 'adShowFail',
  AD_LOAD_FAIL: 'adLoadFail'
});
/**
 * Facebook ad types
 */

const FBAdTypes = Object.freeze({
  REWARDED: 'rewarded',
  INTERSTITIAL: 'interstitial'
});

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

/* eslint-disable import/no-cycle */

/* eslint-disable no-underscore-dangle */

/**
 * Class for storing data / references related to FB FBInstant.AdInstances.
 */
class FBAdContainer {
  constructor(fbAdInstance, state, placement) {
    this._fbAdInstance = fbAdInstance;
    this._state = state;
    this._placement = placement;
  }
  /**
   * Get the unrwapped Facebook AdInstance.
   */


  get fbAdInstance() {
    return this._fbAdInstance;
  }
  /**
   * Get current state of the FBInstant.AdInstance
   */


  get state() {
    return this._state;
  }
  /**
   * Update the state of the FBInstant.AdInstance
   */


  setState(state) {
    this._state = state;
  }
  /**
   * Get the associated placement
   */


  get placement() {
    return this._placement;
  }

}

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/**
 * Logs a stylized message to the console.
 * @param msg message text
 * @param color text color
 * @param bgColor background / block color for console log
 */

function stylizedLog(msg, color = '#00FF00', bgColor = '#222222') {
  console.log(`%c ${msg} `, `background: ${bgColor}; color: ${color}`);
}
/**
 * clone an Object and return it.
 * @param obj object to clone.
 */

function cloneObject(obj) {
  return JSON.parse(JSON.stringify(obj));
}
/**
 * creates a Promise to wait for a set duration.
 * @param ms time to wait in milliseconds
 */

function waitMs(ms) {
  return __awaiter(this, void 0, void 0, function* () {
    return new Promise(resolve => setTimeout(resolve, ms));
  });
}

const DEFAULT_AD_RETRY_DELAY = 30000;
const DEFAULT_AD_RETRY_ATTEMPTS = 1; // delay / retries for no ad ready timeout

const SHOW_AD_RETRY_DELAY = 1000;
const DEFAULT_SHOW_AD_RETRY_COUNT = 5;
/**
 * Class for managing loading / preloading of FB Instant ads.
 */

class FBAdLoader {
  constructor(config) {
    const {
      placements,
      adRetryDelay = DEFAULT_AD_RETRY_DELAY,
      maxRetryAttempts = DEFAULT_AD_RETRY_ATTEMPTS,
      verbose = true,
      onError = undefined
    } = config;
    this.placements = cloneObject(placements);
    this.adRetryDelay = adRetryDelay;
    this.maxRetryAttempts = maxRetryAttempts;
    this.verbose = verbose;
    this.onError = onError;
    this.init();
  }
  /**
   * Main intialization creates intial FBAdContainers.
   */


  init() {
    return __awaiter(this, void 0, void 0, function* () {
      this.unfilledAdMap = new Map();
      this.adContainers = [];
      this.placements.forEach(placement => {
        if (this.unfilledAdMap.has(placement.id)) return; // for tracking unfilled adContainers. These are reused these for retries.

        this.unfilledAdMap.set(placement.id, []); // create ad instance if being preloaded

        if (placement.preload) this.createAdInstance(placement);
      });
    });
  }
  /**
   * Creates an adInstance of the appropriate type.
   */


  createAdInstance(placement, retries = 0) {
    return __awaiter(this, void 0, void 0, function* () {
      let ad;
      let fbAdInstance;
      const unfilledAds = this.unfilledAdMap.get(placement.id);

      try {
        if (unfilledAds.length > 0) {
          // reuse previous unfilled ads
          ad = unfilledAds.pop();
        } else if (placement.type === FBAdTypes.REWARDED) {
          fbAdInstance = yield FBInstant.getRewardedVideoAsync(placement.id);
          ad = new FBAdContainer(fbAdInstance, 0
          /* IDLE */
          , placement);
          this.adContainers.push(ad);
        } else if (placement.type === FBAdTypes.INTERSTITIAL) {
          // create interstital ad
          fbAdInstance = yield FBInstant.getInterstitialAdAsync(placement.id);
          ad = new FBAdContainer(fbAdInstance, 0
          /* IDLE */
          , placement);
          this.adContainers.push(ad);
        }
      } catch (error) {
        // ad instance could not be created
        this.onAdError(placement, AdErrorEvents.AD_LOAD_FAIL, error);
        return;
      } // begin preloading this ad now


      this.preloadAd(ad, retries);
    });
  }
  /**
   * Get a placement by its name.
   */


  getPlacementByName(name) {
    return this.placements.find(placement => placement.name === name);
  }
  /**
   * Start preloading an ad.
   * @param {FB_AdInstance} ad
   */


  preloadAd(ad, retries = 0) {
    return __awaiter(this, void 0, void 0, function* () {
      const {
        adContainers,
        unfilledAdMap,
        maxRetryAttempts,
        adRetryDelay
      } = this;
      const {
        placement
      } = ad;

      try {
        // attempt to preload
        ad.setState(1
        /* LOADING */
        );
        yield ad.fbAdInstance.loadAsync();
        ad.setState(2
        /* READY */
        );
        this.logSuccessMsg(`FBAdLoader: ad (${placement.type}) loaded successfully`);
      } catch (error) {
        // failed to preload
        ad.setState(0
        /* IDLE */
        );
        this.onAdError(placement, AdErrorEvents.AD_LOAD_FAIL, error);
        const errorMsg = `FBAdLoader: ad { type:${placement.type}, id: ${placement.id} } failed to load (error: ${error.code})`;

        if (retries >= maxRetryAttempts) {
          // max retries hit
          this.logWarningMsg(`${errorMsg}, no retries remaining`);
          if (error.code === AdErrorCodes.ADS_NO_FILL) unfilledAdMap.get(placement.id).push(ad);
          adContainers.splice(adContainers.indexOf(ad), 1);
        } else {
          // retry loading ad
          this.logWarningMsg(`${errorMsg}, retry in ${adRetryDelay} ms.`);

          if (error.code === AdErrorCodes.ADS_NO_FILL) {
            // call loadAsync since ad not be filled
            yield waitMs(adRetryDelay);
            this.preloadAd(ad, retries + 1);
          } else {
            // other errors re-create the ad and remove this ad instance
            adContainers.splice(adContainers.indexOf(ad), 1);
            yield waitMs(adRetryDelay);
            this.createAdInstance(ad.placement, retries + 1);
          }
        }
      }
    });
  }
  /**
   * get a preloaded adInstance of a specific type
   */


  getPreloadedAd(adType) {
    const {
      adContainers
    } = this;

    const adFilter = ({
      placement,
      state
    }) => placement.type === adType && state === 2
    /* READY */
    ;

    const preloadedAds = adContainers.filter(adFilter);
    const ad = preloadedAds.length > 0 ? preloadedAds.shift() : null;
    if (ad) adContainers.splice(adContainers.indexOf(ad), 1);
    return ad;
  }
  /**
   * Get instance count of a specific ad placement.
   */


  getAdInstanceCount(placementId) {
    return this.adContainers.filter(ad => ad.placement.id === placementId).length;
  }
  /**
   * Show an ad by its set name. You dont really need to await this since facebook locks UI.
   */


  showAd(placementName, onAdSuccess, onAdFail, maxRetryCount = DEFAULT_SHOW_AD_RETRY_COUNT) {
    return __awaiter(this, void 0, void 0, function* () {
      const placement = this.getPlacementByName(placementName);

      if (!placement) {
        throw new Error(`Error no placement with name: ${placementName} was found.`);
      }

      let retryCount = maxRetryCount; // create on demand instance if we are not preloading

      let adInstanceCount = this.getAdInstanceCount(placement.id);

      if (adInstanceCount === 0 && retryCount === maxRetryCount) {
        this.createAdInstance(placement);
        adInstanceCount += 1;
      } // get a preloaded ad


      const ad = this.getPreloadedAd(placement.type);

      if (!ad) {
        // no ad instance available
        if (retryCount > 0) {
          // retry a few times before failing
          retryCount -= 1;
          yield waitMs(SHOW_AD_RETRY_DELAY);
          yield this.showAd(placement.name, onAdSuccess, onAdFail, retryCount);
        } else {
          // no retries left just fail
          onAdFail();
          this.onAdError(placement, AdErrorEvents.AD_SHOW_FAIL, null, AdErrorCodes.TIME_OUT);
        }
      } else {
        // show pre-loaded ad now
        try {
          // show the add now
          yield ad.fbAdInstance.showAsync(); // begin preloading next ad for the placement

          if (placement.preload) this.createAdInstance(placement);
          onAdSuccess();
        } catch (error) {
          // if ad was cancelled by the user begin preloading next ad
          if ((error === null || error === void 0 ? void 0 : error.code) === AdErrorCodes.USER_INPUT && placement.preload) {
            this.createAdInstance(placement);
          }

          this.onAdError(placement, AdErrorEvents.AD_SHOW_FAIL, error);
          onAdFail();
        }
      }
    });
  }
  /**
   * Log a stylized error in the ad load / show flow.
   */


  onAdError(placement, eventName, error = null, altCode = AdErrorCodes.NO_ERROR_CODE) {
    const errorCode = (error === null || error === void 0 ? void 0 : error.code) || altCode;

    if (this.verbose) {
      this.logErrorMsg(`FBAdLoader: Error { placementId:${placement.id}, type:${placement.type}, eventName: ${eventName}, code:${errorCode} }`);
    } // execute onError method if set


    if (this.onError) {
      this.onError(eventName, placement.id, errorCode);
    }
  }
  /**
   * Log a stylized error message.
   */


  logErrorMsg(msg) {
    if (!this.verbose) return; // eslint-disable-next-line no-console

    console.error(msg);
  }
  /**
   * Log a stylized success message.
   */


  logSuccessMsg(msg) {
    if (!this.verbose) return;
    stylizedLog(msg, '#00FF00');
  }
  /**
   * Log a stylized warning message.
   */


  logWarningMsg(msg) {
    if (!this.verbose) return;
    stylizedLog(msg, '#FFFF00');
  }

}

export { AdErrorEvents, FBAdLoader, FBAdTypes };
//# sourceMappingURL=fb-ad-preloader.js.map
